import {
  Close,
  Map,
  Public,
  LocationCity,
  Home,
  Lan,
  DeviceHub
} from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Autocomplete,
  Skeleton,
  Chip,
  Paper,
  useTheme,
  IconButton,
  Stack,
  alpha,
  Divider,
} from "@mui/material";
import { useState, useEffect } from "react";
import useStyles from "./styles";
import api from "../../../services/api";

const FILTER_CATEGORIES = [
  { key: "estados", label: "Estado", icon: Public },
  { key: "cidades", label: "Cidade", icon: LocationCity },
  { key: "bairros", label: "Bairro", icon: Home },
  { key: "pops", label: "POP", icon: Lan },
  { key: "olts", label: "OLT", icon: DeviceHub },
];

const CACHE_KEY = "ftth_filterOptions";
const CACHE_TTL = 60 * 60 * 1000;

const FILTER_DATA_OBJ = {
  estados: "estados",
  cidade: "cidades",
  bairro: "bairros",
  pop: "pops",
  olt: "olts",
};

export default function NewMapFtth({
  openNovoMapaDialog = false,
  setOpenNovoMapaDialog = () => {},
  setSnackbar = () => {},
  addMapInMaps = () => {},
  updateTitleInMaps = () => {},
  editMode = false,
  initialData = null,
  mapIndex = null,
}) {
  const [titulo, setTitulo] = useState("");
  const [selectedFilters, setSelectedFilters] = useState({});
  const [enabledCategories, setEnabledCategories] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [filterOptions, setFilterOptions] = useState({});

  const theme = useTheme();
  const styles = useStyles(theme);

  useEffect(() => {
    if (openNovoMapaDialog) {
      fetchFilterOptions();
      if (editMode && initialData) {
        setTitulo(initialData.titulo || "");
        const initFilters = initialData.ftthFilter || {};
        setSelectedFilters(initFilters);
        setEnabledCategories(Object.keys(initFilters));
      } else {
        clearDialog();
      }
    }
  }, [openNovoMapaDialog, editMode, initialData]);

  useEffect(() => {
    fetchFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function clearDialog() {
    setTitulo("");
    setSelectedFilters({});
    setEnabledCategories([]);
  }

  async function fetchFilterOptions() {
    try {
      const cachedRaw = sessionStorage.getItem(CACHE_KEY);
      if (cachedRaw) {
        const cached = JSON.parse(cachedRaw);
        if (cached.timestamp && Date.now() - cached.timestamp < CACHE_TTL) {
          setFilterOptions(cached.data);
          setLoadingFilters(false);
          return;
        }
      }
    } catch {
      // ignore parse errors and fallback to API call
    }

    setLoadingFilters(true);
    try {
      const response = await api.get("/easyMaps/onus/filters");
      const backendData = response.data || {};

      const mappedData = {};
      Object.entries(FILTER_DATA_OBJ).forEach(([backendKey, frontendKey]) => {
        if (Array.isArray(backendData[backendKey])) {
          mappedData[frontendKey] = backendData[backendKey];
        } else {
          mappedData[frontendKey] = [];
        }
      });

      setFilterOptions(mappedData);
      sessionStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ data: mappedData, timestamp: Date.now() })
      );
    } catch (error) {
      console.error("Failed to load filter options:", error);
      setSnackbar({
        children: "Erro: Não foi possível carregar os filtros",
        severity: "error",
      });
    } finally {
      setLoadingFilters(false);
    }
  }

  const handleClose = () => {
    setOpenNovoMapaDialog(false);
    clearDialog();
  };

  function handleToggleCategory(key) {
    setEnabledCategories((prev) => {
      if (prev.includes(key)) {
        setSelectedFilters((s) => {
          const next = { ...s };
          delete next[key];
          return next;
        });
        return prev.filter((k) => k !== key);
      }
      return [...prev, key];
    });
  }

  function handleFilterChange(key, newValue) {
    setSelectedFilters((prev) => ({
      ...prev,
      [key]: newValue,
    }));
  }

  async function handleApiNewMap() {
    try {
      setLoadingSubmit(true);
      const logsString = localStorage.getItem("logs");
      const logs = logsString ? JSON.parse(logsString) : null;

      const ftthFilterConfig = {};
      Object.entries(selectedFilters).forEach(([key, value]) => {
        if (value && value.length > 0) {
          ftthFilterConfig[key] = value;
        }
      });

      if (editMode) {
        const response = await api.put("/easyMaps/map/updateTitle", {
          mapId: initialData.id,
          titulo,
          logsLoginClienteId: logs?.id || "",
          ftthFilterConfig,
        });

        if (response.data.status === "Error") {
          setSnackbar({
            children: "Error: Não foi possível atualizar o mapa",
            severity: "error",
          });
        } else {
          if (response.data) {
            updateTitleInMaps(titulo, initialData.id, ftthFilterConfig);
            setSnackbar({
              children: "Mapa atualizado com sucesso!",
              severity: "success",
            });
            clearDialog();
            setOpenNovoMapaDialog(false);
          }
        }
      } else {
        const response = await api.post("/easyMaps/map/create", {
          titulo,
          tipo: 3,
          logsLoginClienteId: logs?.id || "",
          ftthFilterConfig,
        });

        if (response.data.status === "Error") {
          setSnackbar({
            children: "Error: Não foi possível criar mapa",
            severity: "error",
          });
        } else {
          if (response.data) {
            // O backend retorna apenas o mapa cru (sem ftthFilter). Mesclamos o
            // filtro recém-criado para que o card já apareça como mapa com
            // filtros imediatamente, sem precisar recarregar a página.
            addMapInMaps({ ...response.data, ftthFilter: ftthFilterConfig });
            clearDialog();
            setOpenNovoMapaDialog(false);
          }
        }
      }
    } catch (error) {
      console.error(error);
      if (error?.response?.status === 400 || error?.response?.status === 409) {
        setSnackbar({
          children:
            "Error: Já existe um mapa com esse nome. Escolha um nome diferente.",
          severity: "error",
        });
      } else {
        setSnackbar({
          children: `Error: ${
            error?.message || "Não foi possível se conectar com o servidor"
          }`,
          severity: "error",
        });
      }
    } finally {
      setLoadingSubmit(false);
    }
  }

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleApiNewMap();
    }
  };

  const enabledEntries = enabledCategories.map((key) =>
    FILTER_CATEGORIES.find((c) => c.key === key),
  );

  const hasActiveFilters = enabledEntries.length > 0;
  const hasTitle = titulo.trim().length > 0;

  return (
    <Dialog
      open={openNovoMapaDialog}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={styles.dialogTitle}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: "primary.main",
              }}
            >
              <Map fontSize="small" />
            </Box>
            <Box>
              <Typography
                component="div"
                variant="h6"
                sx={{ fontWeight: 700, fontSize: "1.15rem", lineHeight: 1.3 }}
              >
                {editMode ? "Editar mapa FTTH" : "Novo mapa FTTH"}
              </Typography>
              <Typography
                component="div"
                variant="body2"
                sx={{ color: "text.secondary", fontSize: "0.8rem", mt: 0.25 }}
              >
                {editMode ? "Altere os filtros de visualização" : "Configure os filtros para gerar seu mapa de fibra"}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{ mt: 0.5, color: "text.secondary" }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={styles.dialogContent}>
        <Stack spacing={2} divider={<Divider sx={{ opacity: 0.6 }} />}>
          {/* Section 1: Title */}
          <Box>
            <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
              <Box sx={styles.sectionNumber}>1</Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Nome do mapa
              </Typography>
            </Stack>
            <TextField
              value={titulo}
              size="small"
              onKeyDown={handleKeyDown}
              sx={styles.textfield}
              autoComplete="off"
              placeholder="Ex: Mapa FTTH São Paulo"
              fullWidth
              onChange={(event) => {
                const re = (s) =>
                  s.replace(
                    /(?:\p{Extended_Pictographic}|\uFE0F|\u200D|\u{1F3FB}-\u{1F3FF})+/gu,
                    "",
                  );
                setTitulo(re(event.target.value));
              }}
            />
          </Box>

          {/* Section 2: Filters */}
          <Box>
            <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
              <Box sx={styles.sectionNumber}>2</Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Filtros de visualização
              </Typography>
            </Stack>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1.5, fontSize: "0.8rem" }}
            >
              Selecione os critérios que deseja aplicar ao mapa. Você pode
              combinar vários filtros para refinar a visualização.
            </Typography>

            {loadingFilters ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(3, 1fr)",
                    sm: "repeat(5, 1fr)",
                  },
                  gap: 1,
                }}
              >
                {FILTER_CATEGORIES.map(({ key }) => (
                  <Skeleton
                    key={key}
                    variant="rectangular"
                    sx={{ width: "100%", height: 72, borderRadius: 2 }}
                  />
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(3, 1fr)",
                    sm: "repeat(5, 1fr)",
                  },
                  gap: 1,
                }}
              >
                {FILTER_CATEGORIES.map(({ key, label, icon: Icon }) => {
                  const isEnabled = enabledCategories.includes(key);                  
                  return (
                    <Paper
                      key={key}
                      onClick={() => handleToggleCategory(key)}
                      elevation={0}
                      sx={{
                        p: 1,
                        cursor: "pointer",
                        borderRadius: 1,
                        border: "1.5px solid",
                        borderColor: isEnabled
                          ? "primary.main"
                          : "divider",
                        bgcolor: isEnabled
                          ? alpha(theme.palette.primary.main, 0.06)
                          : "background.paper",
                        transition: "all 0.2s ease",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0.75,
                        position: "relative",
                        minWidth: 0,
                        "&:hover": {
                          borderColor: isEnabled
                            ? "primary.dark"
                            : "text.disabled",
                          bgcolor: isEnabled
                            ? alpha(theme.palette.primary.main, 0.09)
                            : alpha(theme.palette.action.hover, 0.05),
                        },
                      }}
                    >
                      <Icon
                        fontSize="small"
                        sx={{
                          color: isEnabled
                            ? "primary.main"
                            : "text.secondary",
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: isEnabled ? 600 : 500,
                          color: isEnabled
                            ? "primary.main"
                            : "text.primary",
                          fontSize: "0.8rem",
                          textAlign: "center",
                          lineHeight: 1.2,
                        }}
                      >
                        {label}
                      </Typography>
                    </Paper>
                  );
                })}
              </Box>
            )}

            {/* Active filter inputs */}
            {hasActiveFilters && (
              <Stack spacing={2} sx={{ mt: 2.5 }}>
                {enabledEntries.map(({ key, label, icon: Icon }) => {
                  const currentValue = selectedFilters[key] || [];
                  return (
                    <Paper
                      key={key}
                      variant="outlined"
                      sx={{
                        borderRadius: 1,
                        overflow: "hidden",
                        borderColor: theme.palette.background.default,
                      }}
                    >
                      <Box
                        sx={{
                          px: 1,
                          py: 0.5,
                          bgcolor: alpha(
                            theme.palette.background.default,
                            1,
                          ),
                          borderBottom: "1px solid",
                          borderColor: alpha(
                            theme.palette.background.default,
                            1,
                          ),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          gap={1}
                        >
                          <Icon
                            fontSize="small"
                            color="primary"
                          />
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600 }}
                          >
                            {label}
                          </Typography>
                          {currentValue.length > 0 && (
                            <Chip
                              label={`${currentValue.length} selecionado${
                                currentValue.length > 1 ? "s" : ""
                              }`}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{
                                height: 20,
                                fontSize: "0.7rem",
                                "& .MuiChip-label": {
                                  px: 1,
                                },
                              }}
                            />
                          )}
                        </Stack>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleCategory(key)}
                          sx={{ color: "text.secondary" }}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>
                      <Box sx={{ p: 0}}>
                        <Autocomplete
                          multiple
                          size="small"
                          disableCloseOnSelect
                          options={filterOptions[key] || []}
                          value={currentValue}
                          onChange={(_, newValue) =>
                            handleFilterChange(key, newValue)
                          }
                          getOptionLabel={(option) => option}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              sx={styles.textfield}
                              placeholder={`Selecionar ${label.toLowerCase()}`}
                            />
                          )}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                label={option}
                                size="small"
                                {...getTagProps({ index })}
                                key={index}
                                sx={{
                                  fontSize: "0.75rem",
                                  height: 24,
                                }}
                              />
                            ))
                          }
                        />
                      </Box>
                    </Paper>
                  );
                })}
              </Stack>
            )}

            {!hasActiveFilters && !loadingFilters && (
              <Box
                sx={{
                  textAlign: "center",
                  py: 3,
                  px: 2,
                  color: "text.secondary",
                  border: "1px dashed",
                  borderColor: "divider",
                  borderRadius: 2,
                  mt: 2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.85rem",
                    color: "text.disabled",
                  }}
                >
                  Selecione os filtros acima para começar a configurar seu
                  mapa FTTH
                </Typography>
              </Box>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={styles.dialogActions}>
        <Button
          onClick={handleClose}
          sx={{ textTransform: "none", borderRadius: 2, px: 2 }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          disableElevation
          sx={{
            textTransform: "none",
            borderRadius: 2,
            px: 3,
          }}
          onClick={handleApiNewMap}
          disabled={loadingSubmit || !hasTitle}
        >
          {loadingSubmit ? "Salvando..." : editMode ? "Salvar" : "Criar mapa"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
